import { createTheme, PaletteMode } from '@mui/material/styles';
import { projectConfig, ThemeTokens } from '../config/projectConfig';

declare module '@mui/material/styles' {
  interface Theme {
    HEPalette: {
      text: {
        primary: string;
        secondary: string;
      };
      gradients: {
        dark: {
          main: string;
          state: string;
        };
      };
      coloredShadows: {
        primary: string;
        secondary: string;
        info: string;
        success: string;
        warning: string;
        error: string;
        light: string;
        dark: string;
      };
      sidebar: {
        background: string;
        backgroundDark: string;
        text: string;
        textSecondary: string;
        hover: string;
        active: string;
        border: string;
      };
    };
  }

  interface ThemeOptions {
    HEPalette?: {
      text?: {
        primary?: string;
        secondary?: string;
      };
      gradients?: {
        dark?: {
          main?: string;
          state?: string;
        };
      };
      coloredShadows?: {
        primary?: string;
        secondary?: string;
        info?: string;
        success?: string;
        warning?: string;
        error?: string;
        light?: string;
        dark?: string;
      };
      sidebar?: {
        background?: string;
        backgroundDark?: string;
        text?: string;
        textSecondary?: string;
        hover?: string;
        active?: string;
        border?: string;
      };
    };
  }
}

const lightThemeTokens: ThemeTokens = projectConfig.theme;

const darkThemeTokens: ThemeTokens = {
  ...projectConfig.theme,
  name: `${projectConfig.theme.name} Dark`,
  colors: {
    ...projectConfig.theme.colors,
    primary: {
      main: '#C9956A',
      light: '#E2B88F',
      dark: '#A7744C',
      contrastText: '#1C1814',
    },
    secondary: {
      main: '#B0A89E',
      light: '#D3CBC1',
      dark: '#8B8379',
      contrastText: '#1C1814',
    },
    error: {
      main: '#E57A73',
      light: '#F1A09A',
      dark: '#B95A54',
      contrastText: '#1C1814',
    },
    warning: {
      main: '#E2AE62',
      light: '#EFC98E',
      dark: '#BE8D49',
      contrastText: '#1C1814',
    },
    info: {
      main: '#8CB9D9',
      light: '#B7D3E7',
      dark: '#5E87A4',
      contrastText: '#1C1814',
    },
    success: {
      main: '#70B690',
      light: '#9DCDAF',
      dark: '#4F8F6D',
      contrastText: '#1C1814',
    },
    background: {
      default: '#1C1814',
      paper: '#231F1A',
    },
    text: {
      primary: '#EDE6DC',
      secondary: '#B0A89E',
    },
  },
  customPalette: {
    gradients: {
      dark: {
        main: '#1C1814',
        state: '#231F1A',
      },
    },
    coloredShadows: {
      primary: 'rgba(201, 149, 106, 0.28)',
      secondary: 'rgba(176, 168, 158, 0.22)',
      info: 'rgba(140, 185, 217, 0.2)',
      success: 'rgba(112, 182, 144, 0.22)',
      warning: 'rgba(226, 174, 98, 0.22)',
      error: 'rgba(229, 122, 115, 0.22)',
      light: 'rgba(176, 168, 158, 0.16)',
      dark: 'rgba(15, 11, 8, 0.5)',
    },
    sidebar: {
      background: '#1F1B16',
      backgroundDark: '#1C1814',
      text: '#EDE6DC',
      textSecondary: '#B0A89E',
      hover: 'rgba(201, 149, 106, 0.10)',
      active: 'rgba(201, 149, 106, 0.18)',
      border: 'rgba(201, 149, 106, 0.14)',
    },
  },
};

const getThemeTokens = (mode: PaletteMode) => (mode === 'dark' ? darkThemeTokens : lightThemeTokens);

export const getThemeColor = (mode: PaletteMode) => getThemeTokens(mode).colors.primary.main;

export const createAppTheme = (mode: PaletteMode) => {
  const activeTheme = getThemeTokens(mode);
  const isDarkMode = mode === 'dark';

  return createTheme({
    spacing: activeTheme.spacing,
    palette: {
      mode,
      primary: activeTheme.colors.primary,
      secondary: activeTheme.colors.secondary,
      error: activeTheme.colors.error,
      warning: activeTheme.colors.warning,
      info: activeTheme.colors.info,
      success: activeTheme.colors.success,
      background: activeTheme.colors.background,
      text: activeTheme.colors.text,
      divider: isDarkMode ? 'rgba(201, 149, 106, 0.18)' : 'rgba(15, 23, 42, 0.08)',
      action: {
        hover: isDarkMode ? 'rgba(201, 149, 106, 0.08)' : 'rgba(15, 23, 42, 0.04)',
        selected: isDarkMode ? 'rgba(201, 149, 106, 0.14)' : 'rgba(2, 136, 209, 0.12)',
        disabledBackground: isDarkMode ? 'rgba(201, 149, 106, 0.12)' : 'rgba(15, 23, 42, 0.08)',
      },
    },
    typography: {
      fontFamily: activeTheme.typography.fontFamily,
      fontSize: activeTheme.typography.fontSize,
      h1: {
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        fontWeight: 700,
      },
      h2: {
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        fontWeight: 700,
      },
      h3: {
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        fontWeight: 700,
      },
      h4: {
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        fontWeight: 800,
        letterSpacing: '-0.03em',
      },
      h5: {
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h6: {
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        fontWeight: 700,
      },
      button: {
        fontWeight: 700,
      },
    },
    shape: {
      borderRadius: 14,
    },
    HEPalette: {
      text: activeTheme.colors.text,
      gradients: activeTheme.customPalette.gradients,
      coloredShadows: activeTheme.customPalette.coloredShadows,
      sidebar: activeTheme.customPalette.sidebar,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: activeTheme.colors.background.default,
            backgroundImage: isDarkMode
              ? 'radial-gradient(circle at top, rgba(201, 149, 106, 0.14), transparent 30%), linear-gradient(180deg, rgba(35, 31, 26, 0.98), rgba(28, 24, 20, 1))'
              : 'linear-gradient(180deg, rgba(244, 247, 251, 1), rgba(237, 243, 249, 1))',
            color: activeTheme.colors.text.primary,
            transition: 'background-color 180ms ease, color 180ms ease',
          },
          '#root': {
            minHeight: '100vh',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: isDarkMode ? '0 18px 40px rgba(12, 9, 7, 0.38)' : '0 16px 36px rgba(15, 23, 42, 0.08)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? 'rgba(237, 230, 220, 0.04)' : activeTheme.colors.background.paper,
            transition: 'background-color 180ms ease, border-color 180ms ease',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backdropFilter: 'blur(10px)',
            backgroundColor: isDarkMode ? 'rgba(35, 31, 26, 0.94)' : 'rgba(255, 255, 255, 0.94)',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDarkMode ? '#edf4fb' : '#111827',
            color: isDarkMode ? '#07111d' : '#f8fafc',
            fontWeight: 600,
          },
        },
      },
    },
  });
};

const theme = createAppTheme('light');

export default theme;
